// @ts-nocheck
import {
  InsuranceType,
  PhysicianForm,
  EnterMedicaidInsuranceForm,
  EnterInsuranceFormV4,
  InsuranceNetwork,
} from 'domains/signup/data/types';
import { AcceptedInsurancesV4, getMedicaidProviderListLogicV4 } from 'domains/signup/insurance/data/insuranceV4';
import { SelectOption } from 'components/Forms';
import { MachineStateType } from 'utils/xstate/types';
import { assign, DoneActorEvent, setup } from 'xstate';
import { checkEligibilityLogic } from '../Parent/actors';
import { TypeInsuranceFields } from 'types/contentful';

export type CompletedInsuranceMachineData = {
  acceptedInsuranceEntries?: TypeInsuranceFields[];
  acceptedInsuranceOptions?: SelectOption[];
  insuranceType: InsuranceType;
  insuranceForm: EnterMedicaidInsuranceForm;
  insuranceEligibility: InsuranceNetwork;
  isInsuranceFormCompleted: boolean;
  medicaidProvider: string;
  primaryPhysician: PhysicianForm;
  state: string;
  useInsurance: boolean;
};

export type InsuranceResult = {
  data: CompletedInsuranceMachineData;
  earlyExit?: boolean;
};

// Machine's input
export type InsuranceMachineInput = {
  prevState?: Partial<CompletedInsuranceMachineData>;
  canExitByBack?: boolean;
};

type InternalContext = {
  data: Partial<CompletedInsuranceMachineData>;
  earlyExit?: boolean;
} & Pick<InsuranceMachineInput, 'canExitByBack'>;

/** @xstate-layout N4IgpgJg5mDOIC5QEsB2sCuAnAhqgxmALI74AWaYAdLAC45a0DEA2gAwC6ioADgPaxktZH1TcQAD0QAmABwBWKvNlsA7NICcARgAsbfQDZZOgDQgAnoh2yNVVXoUBmDeoPyAvu7NpMuAsVIKVGo6BmYWLS4kEH5BYVFxKQQ5RWU1TV19NiNTC0RHRx0qbINHVTZ5Nh1qlwNPb3RsPEISckoqMFRaMCwAVVgwAElGv0ImTAAjAFshfqGR5rB2KN4BIRExaKTVUqoNAwMtWTcjCrYtM0sER2ktKgMdNMd5DWtZaVUPLxAfJv9WoLUTrdPoDYa+RbjDDTWZghb+CIrGJreKbUDbXb7Q7HeSnSoXPIIWR3G5yFw7bLveRaeo-eEtQLtYE9Obgv5jCakADWy3EsXWCS2iB2jj2ByOJxU+MuiC0OyUqmJbEc73ObGk0kctN+owCbWCHS6PTZuoAKuYeGAoTDaCbFubLbzovzUYlEApbBpibJjMpSvpHDKEG5FDoDPYtLcNI5SvIdNr6XrAYaQXb-A6rZMZrbExnEXyURs3QhdAcqI42BovR8DBqDrIg2GiqpVIUStJHhppHVvjrFgCmUasGnCBmmJz8DzOAW4kWhSWtDcqFoNPJnD7I1psvIgx87rIChWvapVxrsgmIf9GQbmVgAApYZBTBjmO9kcyCfDIPDW7MPp8vm+H7IF+eBOqss6CuiMgKEoKjqNoeiGMYQYuIobgVJoLzGHI0gXuySaDiC-7Plgr7vp+36oOO3LgcikFopIMGpPBGRIZSuRXGuijZFoWhuAULzyF8DSXgy+pAkORCQCBODIBAD58AAbvJPS-kI0kQLJ8mKSpEA9HRLpztBCAimK2KSmcBJXD6RQ6DGi4rjsei4vhuoDjeUkyfgckKVgymqVg6m0Jp2l+QF+lYPmzqFlBTGmZi4o4ni5xBp8bB2HWRzSGuFTUm5-bXpJIKhT5On+XpakTlOSJGXFSQ6N2VBhtiiofD6xgGKhOVUAeKjPD6FbhiJdJiYRnkld5vm6YFTBTFN5URT0ACiWD+VF04xQxxZmViEq4lKqWEi4tgGNoBT9coOVar2iYedQfb+AAcnwOZPWAtAAO58FgXLjOQkAYAANmAADqQhkP+Sk4N0d44OYhmxYxSSlgY5aVtWOx1kYaXHHY1KaDsXZygcnjfKgfD6fA0SPeJgIzgKyOIAAtF1hKs8UWRc9z8iqAVV4STQ9CMAzrrznKdyvCe1Inh8cYNoS1h3B2kafIUbhhjdokEfdKYsnCY2i8Z8VxtIy4rsc3aHCqlZBgUkvEuocbCSkeG3WNuu3iOYAZkb9Wyh2oo6BolbqJq9irjuiuyKo9y4rccaOIuOj8SNtPjcVPQkYBFEgVRftMwgjWocYezHE2Oj2InNLuzrRV61gpXTRVgUF8W8gdsUvMFCn+irpGqFdnsMvvB8yqfHztfufX6cAMJ8FMPAg90bfi8StgVpXnXZOq6i42bKod5XBjqucbj83T7Tpy9b0fd9v2ryZkbOHYOKPGwCj8RouNo9oe64kcG4NgybuCAA */
export const insuranceMachineV4 = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitUseInsurance'; useInsurance: boolean }
      | { type: 'submitInsuranceType'; insuranceType: InsuranceType }
      | { type: 'submitMedicaidProvider'; medicaidProvider: string }
      | { type: 'submitPrimaryPhysician'; primaryPhysician?: PhysicianForm }
      | { type: 'scheduleWithPrivatePay' }
      | { type: 'medicaidProviderError' }
      | { type: 'submitInsuranceInfo'; insuranceInfo: EnterMedicaidInsuranceForm },

    input: {} as InsuranceMachineInput,
    output: {} as InsuranceResult,
  },

  actors: {
    getMedicaidProviderList: getMedicaidProviderListLogicV4,
    checkEligibility: checkEligibilityLogic,
  },

  actions: {
    saveUseInsurance: assign(({ context }, params: { useInsurance: boolean }) => ({
      data: { ...context.data, useInsurance: params.useInsurance, isInsuranceFormCompleted: false }, // reset isInsuranceFormCompleted when changing useInsurance
    })),
    saveInsuranceType: assign(({ context }, params: { insuranceType: InsuranceType }) => ({
      data: { ...context.data, insuranceType: params.insuranceType },
    })),
    savePrimaryPhysician: assign(({ context }, params: { primaryPhysician?: PhysicianForm }) => {
      return {
        data: { ...context.data, primaryPhysician: { ...params.primaryPhysician } },
      };
    }),
    saveInsuranceInfo: assign(({ context }, params: { insuranceInfo: EnterMedicaidInsuranceForm }) => ({
      data: {
        ...context.data,
        insuranceForm: params.insuranceInfo,
      },
    })),
    saveAcceptedInsuranceOptions: assign(
      (
        { context },
        params: {
          acceptedInsuranceEntries: TypeInsuranceFields[];
          acceptedInsuranceOptions: SelectOption[];
        },
      ) => ({
        data: {
          ...context.data,
          acceptedInsuranceEntries: params.acceptedInsuranceEntries,
          acceptedInsuranceOptions: params.acceptedInsuranceOptions,
        },
      }),
    ),
    saveInsuranceEligibility: assign(({ context }, params: { insuranceEligibility: InsuranceNetwork }) => {
      return {
        data: {
          ...context.data,
          insuranceEligibility: params.insuranceEligibility,
        },
      };
    }),
    resetInsuranceEligibility: assign(({ context }) => ({
      data: {
        ...context.data,
        insuranceEligibility: undefined,
      },
    })),
    resetInsuranceInfo: assign(({ context }, params?: { resetInsuranceType: boolean }) => ({
      data: {
        ...context.data,
        acceptedInsuranceEntries: undefined,
        acceptedInsuranceOptions: undefined,
        insuranceEligibility: undefined,
        insuranceForm: undefined,
        insuranceType: params?.resetInsuranceType ? undefined : context.data.insuranceType,
        primaryPhysician: undefined,
        subscriber: undefined,
      },
    })),
  },
  guards: {
    canDoEligibilityCheck: ({ context }) => {
      return (
        context.data.useInsurance === true &&
        context.data.insuranceType === 'medicaid' &&
        context.data.insuranceForm?.notInsuranceAvailable === false &&
        context.data.primaryPhysician?.noPrimaryPhysician === false
      );
    },
    canExitEarly: ({ context }) => context.canExitByBack === true,
    isNoPrimaryPhysician: ({ context }) => context.data.primaryPhysician?.noPrimaryPhysician === true,
    isUsingInsurance: (_, params: { useInsurance: boolean }) => params.useInsurance === true,
    isUsingMedicaid: (_, params: { insuranceType: InsuranceType }) => params.insuranceType === 'medicaid',
  },
}).createMachine({
  id: 'insuranceMachine',
  initial: 'start',

  context: ({ input: { prevState, canExitByBack } = {} }) => ({ data: { ...prevState }, canExitByBack }),

  states: {
    start: {
      always: [
        {
          //TODO: For now we are considering only insurance type
          target: 'enterPrimaryPhysician',
          guard: ({ context }) => {
            return context.data.isInsuranceFormCompleted === true && context.data.insuranceType === 'medicaid'; // if insurance physician is completed and insurance type is medicaid, go back to it
          },
          actions: {
            type: 'resetInsuranceEligibility',
          },
        },
        {
          target: 'enterInsuranceType',
          guard: ({ context }) => {
            return context.data.isInsuranceFormCompleted === true && context.data.useInsurance === true; // if user has insurance and is completed, go to insurance type step
          },
        },
        {
          target: 'enterUseInsurance',
        },
      ],
    },
    enterUseInsurance: {
      on: {
        submitUseInsurance: [
          {
            target: 'enterInsuranceType',
            guard: {
              type: 'isUsingInsurance',
              params: ({ event }) => event,
            },
            actions: {
              type: 'saveUseInsurance',
              params: ({ event }) => event,
            },
          },
          {
            // if user is NOT using insurance, go to last step
            target: 'insuranceComplete',
            actions: [
              {
                type: 'saveUseInsurance',
                params: ({ event }) => event,
              },
              {
                type: 'resetInsuranceInfo', // make sure there is no insurance info saved
                params: { resetInsuranceType: true },
              },
            ],
          },
        ],

        back: {
          target: 'finished',
          actions: assign(({ context }) => ({
            earlyExit: true,
            data: { ...context.data, isInsuranceFormCompleted: false },
          })),
          guard: 'canExitEarly',
        },
      },
    },

    enterInsuranceType: {
      on: {
        submitInsuranceType: [
          {
            target: 'loadingMedicaidProviderList',
            guard: {
              type: 'isUsingMedicaid',
              params: ({ event }) => event,
            },
            actions: {
              type: 'saveInsuranceType',
              params: ({ event }) => event,
            },
          },
          {
            target: 'insuranceComplete',
            actions: [
              {
                type: 'saveInsuranceType',
                params: ({ event }) => event,
              },
              {
                type: 'resetInsuranceInfo',
              },
            ],
          },
        ],
        back: 'enterUseInsurance',
      },
    },

    loadingMedicaidProviderList: {
      invoke: {
        src: 'getMedicaidProviderList',
        input: ({ context }) => context,
        onDone: {
          target: 'enterMedicaidInsuranceInfo',
          actions: {
            type: 'saveAcceptedInsuranceOptions',
            params: ({ event }) => {
              const { output } = event as DoneActorEvent<AcceptedInsurancesV4>;

              return {
                acceptedInsuranceEntries: output.entries,
                acceptedInsuranceOptions: output.options,
              };
            },
          },
        },
        onError: 'loadingMedicaidProviderListError',
      },
    },

    loadingMedicaidProviderListError: {
      after: {
        500: 'insuranceComplete',
      },
    },

    enterMedicaidInsuranceInfo: {
      on: {
        submitInsuranceInfo: {
          target: 'enterPrimaryPhysician',
          actions: {
            type: 'saveInsuranceInfo',
            params: ({ event }) => event,
          },
        },
        back: 'enterInsuranceType',
      },
    },

    enterPrimaryPhysician: {
      on: {
        submitPrimaryPhysician: {
          target: 'insuranceComplete',
          actions: [
            {
              type: 'savePrimaryPhysician',
              params: ({ event }) => event,
            },
            assign(({ context }) => ({ data: { ...context.data, isInsuranceFormCompleted: true } })),
          ],
        },
        back: 'enterMedicaidInsuranceInfo',
      },
    },

    insuranceNotInNetwork: {
      on: {
        scheduleWithPrivatePay: 'insuranceComplete',
      },
    },

    insuranceComplete: {
      always: [
        {
          target: 'skippedPrimaryPhysician',
          guard: 'isNoPrimaryPhysician',
        },
        {
          target: 'checkEligibilityInformation',
          guard: 'canDoEligibilityCheck',
        },
        {
          target: 'finished',
        },
      ],
    },

    skippedPrimaryPhysician: {
      after: {
        500: 'finished',
      },
    },

    checkEligibilityInformation: {
      invoke: {
        src: 'checkEligibility',
        input: ({ context }) => {
          const selectedEntry = context.data.acceptedInsuranceEntries?.find(entry => {
            return context.data.insuranceForm?.insurancePlan === entry.payerId;
          });

          return {
            ...context.data.insuranceForm,
            state: context.data.state,
            npi: selectedEntry?.npiOverride || undefined,
            networkStatus: selectedEntry?.networkStatus || 'Out-of-network',
            allowSelfSignUp: selectedEntry?.allowSelfSignUp || false,
          } as EnterInsuranceFormV4;
        },
        onDone: {
          target: 'finished',
          actions: {
            type: 'saveInsuranceEligibility',
            params: ({ event: { output } }) => ({ insuranceEligibility: output }),
          },
        },
        onError: 'checkEligibilityInformationError',
      },
    },

    checkEligibilityInformationError: {
      after: {
        500: 'finished',
      },
    },

    finished: {
      type: 'final',
    },
  },

  output: ({ context }) => {
    const {
      acceptedInsuranceEntries,
      acceptedInsuranceOptions,
      isInsuranceFormCompleted,
      insuranceEligibility,
      insuranceForm,
      insuranceType,
      medicaidProvider,
      primaryPhysician,
      state,
      useInsurance,
    } = context.data as CompletedInsuranceMachineData;
    const result: InsuranceResult = {
      data: {
        acceptedInsuranceEntries,
        acceptedInsuranceOptions,
        isInsuranceFormCompleted,
        insuranceEligibility,
        insuranceForm,
        insuranceType,
        medicaidProvider,
        primaryPhysician,
        state,
        useInsurance,
      },
      earlyExit: context.earlyExit,
    };

    return result;
  },
});

export type InsuranceEntryState = MachineStateType<typeof insuranceMachineV4>;
